import { useCallback, useEffect, useRef, useState } from "react";

/**
 * String key for a row id so number and string ids share one slot.
 *
 * @param {string|number} rowId - Row id.
 * @returns {string}
 */
function rowKey(rowId) {
  return String(rowId);
}

/**
 * Rows to show after the server list changes.
 *
 * When nothing is saving, the server list is shown as-is. A row with a save
 * in flight keeps the copy already on screen, and server rows that are not
 * on screen yet are appended.
 *
 * @param {Array<Object>} incoming - Latest server rows.
 * @param {Array<Object>} current - Rows on screen.
 * @param {Set<string>} savingKeys - Ids with a request in flight.
 * @returns {Array<Object>}
 */
function rowsAfterServerUpdate(incoming, current, savingKeys) {
  if (savingKeys.size === 0) return incoming;
  const byId = new Map(incoming.map((row) => [rowKey(row.id), row]));
  const merged = current.map((row) =>
    savingKeys.has(rowKey(row.id)) ? row : byId.get(rowKey(row.id)) || row
  );
  incoming.forEach((row) => {
    if (!merged.some((item) => rowKey(item.id) === rowKey(row.id))) {
      merged.push(row);
    }
  });
  return merged;
}

/**
 * Local row state for an inline-edit management table.
 *
 * Patches show up immediately. A failed save restores the last row the server
 * accepted, or the row from before this burst of edits. A second edit started
 * while a save is in flight is merged into one follow-up request, and the
 * older response does not overwrite it.
 *
 * @param {Array<Object>} rows - Server rows. Local state adopts this list on the same render, so a cleared draft is not painted before its saved row.
 * @param {(row: Object, patch: Object) => Promise<Object>} onSave - Persists a patch and returns the saved row.
 * @param {(row: Object, patch: Object) => Object} [mergePatch] - Builds the optimistic row. Defaults to spreading the patch.
 * @returns {{
 *   rows: Array<Object>,
 *   saveRow: (row: Object, patch: Object) => Promise<Object|undefined>,
 *   isRowSaving: (rowId: string|number) => boolean,
 * }}
 */
export function useManagementRows(rows, onSave, mergePatch) {
  const [localRows, setLocalRows] = useState(rows);
  const [savingIds, setSavingIds] = useState(() => new Set());
  const rowsRef = useRef(rows);
  const seenRowsRef = useRef(rows);
  const savingRef = useRef(new Set());
  const onSaveRef = useRef(onSave);
  const mergePatchRef = useRef(mergePatch);
  // Row as it was before the current burst of edits, used when every request fails.
  const baselineRef = useRef(new Map());
  // Last response accepted by the server during this burst.
  const lastSavedRef = useRef(new Map());
  // Fields edited while a request for that row is already in flight.
  const queuedRef = useRef(new Map());
  const saveTokenRef = useRef(new Map());
  const runSaveRef = useRef(async () => undefined);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    mergePatchRef.current = mergePatch;
  }, [mergePatch]);

  // Adopt server rows during this render. Waiting for an effect left a frame
  // where the draft was already gone and the saved row was not on screen yet.
  if (seenRowsRef.current !== rows) {
    seenRowsRef.current = rows;
    const next = rowsAfterServerUpdate(rows, localRows, savingRef.current);
    rowsRef.current = next;
    if (next !== localRows) {
      setLocalRows(next);
    }
  }

  /**
   * Replace one row in both the ref and React state.
   *
   * @param {string|number} rowId - Row id.
   * @param {Object} nextRow - Replacement row.
   * @returns {void}
   */
  const replaceRow = useCallback((rowId, nextRow) => {
    const next = rowsRef.current.map((item) =>
      rowKey(item.id) === rowKey(rowId) ? nextRow : item
    );
    rowsRef.current = next;
    setLocalRows(next);
  }, []);

  /**
   * Mark a row as saving and mirror that set into state for the table.
   *
   * @param {string} key - String row id.
   * @param {boolean} saving - Whether a request is in flight.
   * @returns {void}
   */
  const setRowSaving = useCallback((key, saving) => {
    if (saving) {
      savingRef.current.add(key);
    } else {
      savingRef.current.delete(key);
    }
    setSavingIds(new Set(savingRef.current));
  }, []);

  /**
   * Build the row shown while a patch is in flight.
   *
   * @param {Object} row - Current row.
   * @param {Object} patch - Fields to write.
   * @returns {Object}
   */
  const optimisticRow = useCallback((row, patch) => {
    if (typeof mergePatchRef.current === "function") {
      return mergePatchRef.current(row, patch);
    }
    return { ...row, ...patch };
  }, []);

  /**
   * Send one patch, then the edits that arrived while it was in flight.
   *
   * @param {string|number} rowId - Row id.
   * @param {Object} patch - Fields to write.
   * @returns {Promise<Object>} Saved row from the request that finished the burst.
   */
  const runSave = useCallback(
    async (rowId, patch) => {
      const key = rowKey(rowId);
      const token = (saveTokenRef.current.get(key) || 0) + 1;
      saveTokenRef.current.set(key, token);
      setRowSaving(key, true);
      const baseline = baselineRef.current.get(key);

      try {
        const saved = await onSaveRef.current(baseline, patch);
        if (saveTokenRef.current.get(key) !== token) {
          return saved;
        }
        lastSavedRef.current.set(key, saved);
        const queued = queuedRef.current.get(key);
        if (queued) {
          queuedRef.current.delete(key);
          return runSaveRef.current(rowId, queued);
        }
        lastSavedRef.current.delete(key);
        baselineRef.current.delete(key);
        replaceRow(rowId, saved);
        return saved;
      } catch (error) {
        if (saveTokenRef.current.get(key) !== token) {
          throw error;
        }
        const queued = queuedRef.current.get(key);
        if (queued) {
          queuedRef.current.delete(key);
          return runSaveRef.current(rowId, queued);
        }
        const restore =
          lastSavedRef.current.get(key) || baselineRef.current.get(key);
        lastSavedRef.current.delete(key);
        baselineRef.current.delete(key);
        if (restore) {
          replaceRow(rowId, restore);
        }
        throw error;
      } finally {
        if (
          saveTokenRef.current.get(key) === token &&
          !queuedRef.current.has(key)
        ) {
          setRowSaving(key, false);
        }
      }
    },
    [replaceRow, setRowSaving]
  );
  runSaveRef.current = runSave;

  /**
   * Apply a patch locally, then persist it.
   *
   * @param {Object} row - Row being edited.
   * @param {Object} patch - Fields to write.
   * @returns {Promise<Object|undefined>} Saved row, or undefined when this edit joined an in-flight save.
   */
  const saveRow = useCallback(
    async (row, patch) => {
      const rowId = row.id;
      const key = rowKey(rowId);
      const current =
        rowsRef.current.find((item) => rowKey(item.id) === key) || row;
      if (!baselineRef.current.has(key)) {
        baselineRef.current.set(key, current);
      }
      replaceRow(rowId, optimisticRow(current, patch));

      if (savingRef.current.has(key)) {
        queuedRef.current.set(key, {
          ...(queuedRef.current.get(key) || {}),
          ...patch,
        });
        return undefined;
      }

      return runSave(rowId, patch);
    },
    [optimisticRow, replaceRow, runSave]
  );

  /**
   * Whether a row save is in flight.
   *
   * @param {string|number} rowId - Row id.
   * @returns {boolean}
   */
  const isRowSaving = useCallback(
    (rowId) => savingIds.has(rowKey(rowId)),
    [savingIds]
  );

  return { rows: localRows, saveRow, isRowSaving };
}
